import { useState, useEffect } from 'react';

const NetworkCalculator = () => {
  const [ipAddress, setIpAddress] = useState('192.168.1.1');
  const [cidr, setCidr] = useState(24);
  const [subnetMask, setSubnetMask] = useState('255.255.255.0');
  const [hostCount, setHostCount] = useState(254);
  const [networkInfo, setNetworkInfo] = useState({
    networkAddress: '',
    broadcastAddress: '',
    firstHost: '',
    lastHost: '',
    hostCount: 0,
    addressType: ''
  });
  const [error, setError] = useState('');
  const [whoisInfo, setWhoisInfo] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showCidrDropdown, setShowCidrDropdown] = useState(false);

  // Well-known address types
  const knownNetworks = [
    { prefix: '0.0.0.0/8', type: 'Dieses Netzwerk' },
    { prefix: '10.0.0.0/8', type: 'Großer Privater Bereich (RFC 1918)' },
    { prefix: '100.64.0.0/10', type: 'Carrier-Grade NAT (RFC 6598)' },
    { prefix: '127.0.0.0/8', type: 'Loopback (localhost)' },
    { prefix: '169.254.0.0/16', type: 'Link-Local (Auto-Konfiguration)' },
    { prefix: '172.16.0.0/12', type: 'Mittlerer Privater Bereich (RFC 1918)' },
    { prefix: '192.0.0.0/24', type: 'IETF Protokoll Assignments' },
    { prefix: '192.0.2.0/24', type: 'Test-Netze (Dokumentation, TEST-NET-1)' },
    { prefix: '192.88.99.0/24', type: 'Ehemals IPv6 to IPv4 Relay (Deprecated)' },
    { prefix: '192.168.0.0/16', type: 'Kleiner Privater Bereich (RFC 1918)' },
    { prefix: '198.18.0.0/15', type: 'Benchmark Tests für Netzwerke' },
    { prefix: '198.51.100.0/24', type: 'Test-Netze (Dokumentation, TEST-NET-2)' },
    { prefix: '203.0.113.0/24', type: 'Test-Netze (Dokumentation, TEST-NET-3)' },
    { prefix: '224.0.0.0/4', type: 'Multicast' },
    { prefix: '240.0.0.0/4', type: 'Reserved / zukünftige Nutzung' },
    { prefix: '255.255.255.255/32', type: 'Broadcast' }
  ];

  // Validate IP Address
  const isValidIp = (ip) => {
    const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    if (!ipPattern.test(ip)) return false;
    
    const octets = ip.split('.');
    return octets.every(octet => parseInt(octet) >= 0 && parseInt(octet) <= 255);
  };

  // Convert CIDR to Subnet Mask
  const cidrToSubnetMask = (cidr) => {
    const mask = [];
    const fullOctets = Math.floor(cidr / 8);
    const remainder = cidr % 8;
    
    for (let i = 0; i < 4; i++) {
      if (i < fullOctets) {
        mask.push(255);
      } else if (i === fullOctets) {
        mask.push(256 - Math.pow(2, 8 - remainder));
      } else {
        mask.push(0);
      }
    }
    
    return mask.join('.');
  };

  // Convert Subnet Mask to CIDR
  const subnetMaskToCidr = (mask) => {
    const octets = mask.split('.');
    let cidr = 0;
    
    for (let octet of octets) {
      let bits = 0;
      let num = parseInt(octet);
      
      while (num > 0) {
        bits += num & 1;
        num = num >> 1;
      }
      
      cidr += bits;
    }
    
    return cidr;
  };

  // Calculate CIDR based on Host Count
  const hostCountToCidr = (count) => {
    // Special cases
    if (count === 1) return 32;
    if (count === 2) return 31;
    
    // Regular calculation: we need log2(count+2) bits for hosts
    // and 32-bits total in IPv4
    const hostBits = Math.ceil(Math.log2(count + 2));
    return 32 - hostBits;
  };

  // Check if IP is in network
  const isIpInNetwork = (ip, networkPrefix) => {
    const [networkIp, networkCidr] = networkPrefix.split('/');
    
    // Convert IP to numeric value
    const ipOctets = ip.split('.').map(octet => parseInt(octet));
    const ipNumeric = (ipOctets[0] << 24) + (ipOctets[1] << 16) + (ipOctets[2] << 8) + ipOctets[3];
    
    // Convert network IP to numeric value
    const networkOctets = networkIp.split('.').map(octet => parseInt(octet));
    const networkNumeric = (networkOctets[0] << 24) + (networkOctets[1] << 16) + (networkOctets[2] << 8) + networkOctets[3];
    
    // Create mask from CIDR
    const cidrNum = parseInt(networkCidr);
    const mask = -1 << (32 - cidrNum);
    
    // Apply mask to both and compare
    return (ipNumeric & mask) === (networkNumeric & mask);
  };

  // Determine address type
  const getAddressType = (ip) => {
    for (const network of knownNetworks) {
      if (isIpInNetwork(ip, network.prefix)) {
        return network.type;
      }
    }
    return 'Öffentliche IP-Adresse';
  };

  // Validate Subnet Mask
  const isValidSubnetMask = (mask) => {
    const octets = mask.split('.');
    if (octets.length !== 4) return false;
    
    // Check if octets are valid numbers
    if (!octets.every(octet => {
      const num = parseInt(octet);
      return !isNaN(num) && num >= 0 && num <= 255;
    })) return false;
    
    // Convert to binary string
    let binaryMask = '';
    for (let octet of octets) {
      binaryMask += parseInt(octet).toString(2).padStart(8, '0');
    }
    
    // Check if it's a valid subnet mask (continuous 1s followed by continuous 0s)
    const regex = /^1*0*$/;
    return regex.test(binaryMask);
  };

  // Perform WHOIS lookup simulation
  const simulateWhoisLookup = (ip) => {
    // This is a simulation only - in a real app you would call an API
    // Provide fake WHOIS data based on IP ranges
    const firstOctet = parseInt(ip.split('.')[0]);
    
    if (firstOctet >= 1 && firstOctet <= 126) {
      return { asn: "AS" + Math.floor(Math.random() * 65000), org: "IANA Class A Assignment" };
    } else if (firstOctet >= 128 && firstOctet <= 168) {
      return { asn: "AS" + Math.floor(Math.random() * 65000), org: "IANA Class B Assignment" };
    } else if (firstOctet >= 170 && firstOctet <= 191) {
      return { asn: "AS" + Math.floor(Math.random() * 65000), org: "APNIC Regional Assignment" };
    } else if (firstOctet >= 193 && firstOctet <= 223) {
      return { asn: "AS" + Math.floor(Math.random() * 65000), org: "RIPE NCC Regional Assignment" };
    }
    
    return null; // For non-public IPs
  };

  // Calculate network information
  const calculateNetwork = () => {
    if (!isValidIp(ipAddress)) {
      setError('Ungültige IP-Adresse');
      return;
    }
    
    // Convert IP to numeric value
    const ipOctets = ipAddress.split('.').map(octet => parseInt(octet));
    const ipNumeric = (ipOctets[0] << 24) + (ipOctets[1] << 16) + (ipOctets[2] << 8) + ipOctets[3];
    
    // Convert mask to numeric value
    const maskOctets = subnetMask.split('.').map(octet => parseInt(octet));
    const maskNumeric = (maskOctets[0] << 24) + (maskOctets[1] << 16) + (maskOctets[2] << 8) + maskOctets[3];
    
    // Calculate network address
    const networkNumeric = ipNumeric & maskNumeric;
    const networkOctets = [
      (networkNumeric >> 24) & 255,
      (networkNumeric >> 16) & 255,
      (networkNumeric >> 8) & 255,
      networkNumeric & 255
    ];
    const networkAddress = networkOctets.join('.');
    
    // Calculate broadcast address
    const invertedMaskNumeric = ~maskNumeric & 0xFFFFFFFF;
    const broadcastNumeric = networkNumeric | invertedMaskNumeric;
    const broadcastOctets = [
      (broadcastNumeric >> 24) & 255,
      (broadcastNumeric >> 16) & 255,
      (broadcastNumeric >> 8) & 255,
      broadcastNumeric & 255
    ];
    const broadcastAddress = broadcastOctets.join('.');
    
    // Calculate first and last host
    let firstHostNumeric = networkNumeric;
    let lastHostNumeric = broadcastNumeric;
    
    // If CIDR < 31 (networks with at least 2 hosts), add/subtract 1 for the hosts
    if (cidr < 31) {
      firstHostNumeric = networkNumeric + 1;
      lastHostNumeric = broadcastNumeric - 1;
    }
    
    const firstHostOctets = [
      (firstHostNumeric >> 24) & 255,
      (firstHostNumeric >> 16) & 255,
      (firstHostNumeric >> 8) & 255,
      firstHostNumeric & 255
    ];
    const firstHost = firstHostOctets.join('.');
    
    const lastHostOctets = [
      (lastHostNumeric >> 24) & 255,
      (lastHostNumeric >> 16) & 255,
      (lastHostNumeric >> 8) & 255,
      lastHostNumeric & 255
    ];
    const lastHost = lastHostOctets.join('.');
    
    // Calculate total hosts (2^(32-CIDR) - 2)
    // For CIDR 31 networks, there are exactly 2 hosts
    // For CIDR 32 networks, there is exactly 1 host
    let calculatedHostCount;
    if (cidr === 32) {
      calculatedHostCount = 1;
    } else if (cidr === 31) {
      calculatedHostCount = 2;
    } else {
      calculatedHostCount = Math.pow(2, 32 - cidr) - 2;
    }
    
    // Set host count without triggering a loop
    if (calculatedHostCount !== hostCount) {
      setHostCount(calculatedHostCount);
    }
    
    // Get address type
    const addressType = getAddressType(ipAddress);
    
    // Check if we need to do a WHOIS lookup
    let whois = null;
    if (addressType === 'Öffentliche IP-Adresse') {
      whois = simulateWhoisLookup(ipAddress);
      setWhoisInfo(whois);
    } else {
      setWhoisInfo(null);
    }
    
    setNetworkInfo({
      networkAddress,
      broadcastAddress,
      firstHost,
      lastHost,
      hostCount: calculatedHostCount,
      addressType
    });
    
    setError('');
  };

  // Handle CIDR change
  const handleCidrChange = (value) => {
    const newCidr = parseInt(value);
    if (newCidr >= 0 && newCidr <= 32) {
      setCidr(newCidr);
      setSubnetMask(cidrToSubnetMask(newCidr));
      setShowCidrDropdown(false);
    }
  };

  // Handle subnet mask change
  const handleSubnetMaskChange = (value) => {
    if (isValidSubnetMask(value)) {
      setSubnetMask(value);
      setCidr(subnetMaskToCidr(value));
    }
  };

  // Handle host count change
  const handleHostCountChange = (value) => {
    const count = parseInt(value);
    if (!isNaN(count) && count > 0 && count <= 4294967294) {
      setHostCount(count);
      const newCidr = hostCountToCidr(count);
      setCidr(newCidr);
      setSubnetMask(cidrToSubnetMask(newCidr));
    }
  };

  // Open WHOIS popup
  const openWhoisPopup = () => {
    setShowPopup(true);
  };

  // Close WHOIS popup
  const closeWhoisPopup = () => {
    setShowPopup(false);
  };

  // Toggle CIDR dropdown
  const toggleCidrDropdown = () => {
    setShowCidrDropdown(!showCidrDropdown);
  };

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click was outside the dropdown container
      const dropdownContainer = document.getElementById('cidr-dropdown-container');
      if (dropdownContainer && !dropdownContainer.contains(event.target)) {
        setShowCidrDropdown(false);
      }
    };

    // Add the event listener when the dropdown is shown
    if (showCidrDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCidrDropdown]);

  // Handle dropdown button click
  const handleDropdownClick = (e) => {
    e.stopPropagation();
    toggleCidrDropdown();
  };

  // Calculate network whenever inputs change
  useEffect(() => {
    calculateNetwork();
  }, [ipAddress, cidr, subnetMask]);

  return (
    <div className="p-6 max-w-4xl mx-auto rounded-lg shadow-lg bg-gray-50" style={{ fontFamily: 'Open Sans, sans-serif' }}>
      <h2 className="text-3xl font-bold mb-6 text-center" style={{ color: '#155ce7' }}>IPv4 Netzwerk-Rechner</h2>
      
      <div className="mb-6 bg-white p-6 rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <label className="block text-gray-700 font-semibold mb-2">IP-Adresse</label>
            <input
              type="text"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="z.B. 192.168.1.1"
            />
          </div>
          <div className="md:w-32 relative" id="cidr-dropdown-container">
            <label className="block text-gray-700 font-semibold mb-2">CIDR</label>
            <div className="relative">
              <button
                onClick={handleDropdownClick}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white flex justify-between items-center"
                style={{ color: '#155ce7' }}
              >
                <span>/{cidr}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={showCidrDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                </svg>
              </button>
              
              {showCidrDropdown && (
                <div className="absolute top-full left-0 mt-1 w-64 max-h-96 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg z-10">
                  <div className="p-2">
                    {[...Array(33).keys()].map(num => {
                      // Calculate total IPs for this CIDR
                      const totalIPs = num === 32 ? 1 : Math.pow(2, 32 - num);
                      // Get subnet mask for this CIDR
                      const subnetMaskForCidr = cidrToSubnetMask(num);
                      
                      return (
                        <button
                          key={num}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCidrChange(num);
                          }}
                          className={`w-full text-left p-2 mb-1 rounded hover:bg-blue-50 transition-colors ${num === cidr ? 'bg-blue-100 border-l-4 border-blue-500' : ''}`}
                        >
                          <div className="flex">
                            <div className="w-24 font-semibold" style={{ color: '#155ce7' }}>/{num}</div>
                            <div>
                              <div className="text-sm text-gray-500">{totalIPs.toLocaleString()} IPs</div>
                              <div className="text-xs text-gray-400">{subnetMaskForCidr}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Subnetzmaske</label>
            <input
              type="text"
              value={subnetMask}
              onChange={(e) => handleSubnetMaskChange(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Anzahl Hosts</label>
            <input
              type="number"
              value={hostCount}
              onChange={(e) => handleHostCountChange(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              min="1"
              max="4294967294"
            />
          </div>
        </div>
      </div>
      
      {error && (
        <div className="mb-6 p-3 bg-red-100 text-red-700 rounded-md border border-red-300">
          <span className="font-semibold">Fehler:</span> {error}
        </div>
      )}
      
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-semibold mb-4" style={{ color: '#155ce7' }}>Netzwerk Informationen</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
            <span className="font-semibold text-gray-700">Netzadresse:</span>
            <span className="ml-2">{networkInfo.networkAddress}</span>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
            <span className="font-semibold text-gray-700">Broadcast:</span>
            <span className="ml-2">{networkInfo.broadcastAddress}</span>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
            <span className="font-semibold text-gray-700">Host-IPs von:</span>
            <span className="ml-2">{networkInfo.firstHost}</span>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
            <span className="font-semibold text-gray-700">Host-IPs bis:</span>
            <span className="ml-2">{networkInfo.lastHost}</span>
          </div>
        </div>
        
        <div className="p-4 bg-blue-50 rounded-md border border-blue-200 mb-4">
          <span className="font-semibold" style={{ color: '#155ce7' }}>Adresstyp:</span>
          <span className="ml-2">{networkInfo.addressType}</span>
        </div>
        
        {whoisInfo && (
          <div className="p-4 bg-green-50 rounded-md border border-green-200">
            <h4 className="font-semibold text-green-800 mb-2">WHOIS Information</h4>
            <div><span className="font-semibold text-gray-700">ASN:</span> <span className="ml-2">{whoisInfo.asn}</span></div>
            <div><span className="font-semibold text-gray-700">Organisation:</span> <span className="ml-2">{whoisInfo.org}</span></div>
            <div className="mt-3">
              <button 
                onClick={openWhoisPopup}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Weitere Informationen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Popup for WHOIS Information */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-11/12 max-w-4xl h-5/6 flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold" style={{ color: '#155ce7' }}>Detaillierte WHOIS Informationen</h3>
              <button 
                onClick={closeWhoisPopup}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div className="flex-1 p-0 overflow-hidden">
              <iframe 
                src={`https://bgp.he.net/ip/${ipAddress}`} 
                className="w-full h-full border-0"
                title="WHOIS Information"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkCalculator;